import { Component } from 'react';
import { connect } from 'react-redux';

import { bindActionCreators, Dispatch } from 'redux';
import { SetLayoutAction, setLayoutAction } from 'store/actions/layoutActions';
import { IsAnyOpenedModel, Layout, OpenModal, PortType, RootState, UserType } from 'types';
import { MapControlsInterface, MenuControlsInterface, VoidInterface } from 'types/functions';
import { getMode } from 'utils/formHandlers';
import { hasPermission } from 'utils/permission';

interface keyboardManagerProps {
    mode: string;
    setLayoutAction: SetLayoutAction;
    openModelFunction: OpenModal;
    isAnyOpenModel: IsAnyOpenedModel;
    closeModelFunction: VoidInterface;
    mapControls: MapControlsInterface;
    user: UserType;
    port: PortType;
    layout: Layout;
    menuControls: MenuControlsInterface;
}

interface keyboardManagerState {
    mode: string;
}

class keyboardManager extends Component<keyboardManagerProps, keyboardManagerState> {
    constructor(props: keyboardManagerProps) {
        super(props);
        this.state = {
            mode: 'search',
        };
    }

    static getDerivedStateFromProps(props: keyboardManagerProps, state: keyboardManagerState) {
        if (props.mode !== state.mode) {
            return {
                mode: props.mode,
            };
        }

        return null;
    }

    componentDidMount(): void {
        // Add event listener for key presses
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount(): void {
        // Remove the event listener
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    // Handler for key presses
    handleKeyDown = (event: KeyboardEvent) => {
        const target = event.target as HTMLElement;
        if (this.props.isAnyOpenModel()) {
            return;
        }
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
            return;
        }
        const { key } = event;
        switch (key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowRight':
                this.props.mapControls.chart?.updateMatrixIndex(key);
                break;
            case '+':
                switch (this.props.mode) {
                    case 'time':
                    case 'live':
                        this.props.mapControls?.time?.zoomIn();
                        break;
                    case 'search':
                        this.props.mapControls?.chart?.zoomIn();
                        break;
                    case 'playback':
                        this.props.mapControls?.playback?.zoomIn();
                        break;
                }

                break;
            case '-':
                switch (this.props.mode) {
                    case 'time':
                    case 'live':
                        this.props.mapControls?.time?.zoomOut();
                        break;
                    case 'search':
                        this.props.mapControls?.chart?.zoomOut();
                        break;
                    case 'playback':
                        this.props.mapControls?.playback?.zoomOut();
                        break;
                }

                break;
            case 'F8':
                if (!hasPermission(this.props.user, this.props.port.id, ['isAdmin'])) {
                    return;
                }
                this.props.setLayoutAction([
                    {
                        action: 'UPDATE_MENU',
                        data: {
                            name: 'systemConsole',
                            section: 'system',
                            state: true,
                        },
                    },
                ]);
                break;
            case 'Escape':
                {
                    this.props.closeModelFunction();

                    const toCancel: string[] = ['measureLength', 'measureArea', 'drawing'];
                    toCancel.forEach((x) => {
                        if (this.props.layout.sections[getMode(this.props.mode)].tools[x].state) {
                            this.props.setLayoutAction([
                                {
                                    action: 'UPDATE_TOOLS_SECTION',
                                    data: {
                                        tool: 'drawing',
                                        section: getMode(this.props.mode),
                                        name: 'pointer',
                                        state: true,
                                    },
                                },
                                {
                                    action: 'UPDATE_MENU',
                                    data: {
                                        name: x,
                                        section: getMode(this.props.mode),
                                        state: false,
                                    },
                                },
                            ]);

                            this.props.menuControls.drawing?.setDrawingMode('pointer', getMode(this.props.mode));
                        }
                    });
                }
                break;
            case 'Backspace':
                {
                    const toCancel: string[] = ['measureLength', 'measureArea', 'drawing'];
                    toCancel.forEach((x) => {
                        if (this.props.layout.sections[getMode(this.props.mode)].tools[x].state) {
                            this.props.menuControls.drawing?.setDrawingMode(`${x}Mode`, getMode(this.props.mode));
                        }
                    });
                }
                break;
            case 'F1':
                event.preventDefault();
                this.props.openModelFunction(
                    'keyboardShortcuts',
                    0,
                    'medium',
                    {},
                    {
                        onCancel: () => {
                            this.props.openModelFunction('keyboardShortcuts');
                        },
                    },
                    true
                );
                break;
        }
    };

    render() {
        return null;
    }
}

const keyboardManagerStateToProps = (state: RootState) => ({
    menuControls: state.layout.menuControls,
    mode: state.layout.mode,
    user: state.user.user,
    layout: state.layout.layout,
    port: state.port.port,
    isAnyOpenModel: state.layout.isAnyOpenModelFunction,
    openModelFunction: state.layout.openModelFunction,
    closeModelFunction: state.layout.closeModelFunction,
    mapControls: state.map.mapControls,
});

const keyboardManagerDispatchToProps = (dispatch: Dispatch) => ({
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(keyboardManagerStateToProps, keyboardManagerDispatchToProps)(keyboardManager);
